import React, { useEffect, useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { Button, Card, Empty, Skeleton, Table, notification } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { fetchDashboard, fetchHealthReport } from "../services/dashboardAPI";
import { fetchSubmittals } from "../services/submittalsAPI";
import { fetchRFIs } from "../services/rfisAPI";
import { useAuth } from "../hooks/useAuth";
import { canAccessHealthReport } from "../constants/healthReportConstants";
import { exportHealthReportToPDF } from "../utils/pdfUtils";
import ViewRFIModal from "../components/ViewRFIModal";
import ViewSubmittalModal from "../components/ViewSubmittalModal";
import ProjectSiteAddressBanner from "../components/ProjectSiteAddressBanner";

const OPEN_STATUS_FILTER = { status: "Open" };

const Home = () => {
  const { selectedProject, currentUser, setSelectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const projectName = selectedProject?.project_name || "this project";
  const canViewHealthReport = canAccessHealthReport(selectedProject?.role);
  const [selectedHealthReportProjectId, setSelectedHealthReportProjectId] =
    useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showRfiModal, setShowRfiModal] = useState(false);
  const [showSubmittalModal, setShowSubmittalModal] = useState(false);
  const [selectedHealthReportProject, setSelectedHealthReportProject] =
    useState(null);

  useEffect(() => {
    setSelectedHealthReportProjectId(null);
    setSelectedHealthReportProject(null);
  }, [projectId]);

  const {
    data: healthReportData,
    isLoading: isHealthReportLoading,
    isError: isHealthReportError,
    error: healthReportError,
  } = useQuery({
    queryKey: ["health-report"],
    queryFn: fetchHealthReport,
    enabled: canViewHealthReport,
  });

  const effectiveProjectId = selectedHealthReportProjectId || projectId;

  const projectForAddress =
    (effectiveProjectId &&
      currentUser?.projects?.find(
        (p) => p.project_id === effectiveProjectId,
      )) ||
    selectedProject;

  const dashboardQueries = useQueries({
    queries: effectiveProjectId
      ? [
          {
            queryKey: ["dashboard", effectiveProjectId],
            queryFn: () => fetchDashboard(effectiveProjectId),
            enabled: !!effectiveProjectId,
          },
        ]
      : [],
  });
  const {
    data: openSubmittalsData,
    isLoading: isOpenSubmittalsLoading,
    error: openSubmittalsError,
  } = useQuery({
    queryKey: ["dashboard-open-submittals", effectiveProjectId],
    queryFn: async () => {
      const initialResponse = await fetchSubmittals(
        effectiveProjectId,
        1,
        1,
        OPEN_STATUS_FILTER,
      );

      if (
        !initialResponse?.total ||
        initialResponse.total <= initialResponse.submittals.length
      ) {
        return initialResponse;
      }

      return fetchSubmittals(
        effectiveProjectId,
        1,
        initialResponse.total,
        OPEN_STATUS_FILTER,
      );
    },
    enabled: !!effectiveProjectId,
  });
  const {
    data: openRFIsData,
    isLoading: isOpenRFIsLoading,
    error: openRFIsError,
  } = useQuery({
    queryKey: ["dashboard-open-rfis", effectiveProjectId],
    queryFn: async () => {
      const initialResponse = await fetchRFIs(
        effectiveProjectId,
        1,
        1,
        OPEN_STATUS_FILTER,
      );

      if (
        !initialResponse?.total ||
        initialResponse.total <= initialResponse.rfis.length
      ) {
        return initialResponse;
      }

      return fetchRFIs(
        effectiveProjectId,
        1,
        initialResponse.total,
        OPEN_STATUS_FILTER,
      );
    },
    enabled: !!effectiveProjectId,
  });

  const isCourtLoading =
    !!effectiveProjectId &&
    (dashboardQueries[0]?.isLoading ||
      isOpenSubmittalsLoading ||
      isOpenRFIsLoading);
  const courtError = dashboardQueries[0]?.isError
    ? dashboardQueries[0]?.error
    : openSubmittalsError || openRFIsError || null;
  const dashboardData = dashboardQueries[0]?.data;

  if (isCourtLoading) {
    return (
      <div className="dashboard dashboard-home">
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <ProjectSiteAddressBanner project={projectForAddress} />
        <Skeleton
          active
          title={{ width: "20%" }}
          paragraph={false}
          className="mb-8"
        />
        {canViewHealthReport && (
          <div className="dashboard-table-section">
            <div className="flex gap-4 mb-4">
              <Skeleton.Button active />
            </div>
            <Card className="dashboard-table-card">
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          </div>
        )}
        <div className="dashboard-court-grid">
          <Card className="dashboard-table-card dashboard-court-card">
            <Skeleton active paragraph={{ rows: 3 }} />
          </Card>
          <Card className="dashboard-table-card dashboard-court-card">
            <Skeleton active paragraph={{ rows: 3 }} />
          </Card>
        </div>
      </div>
    );
  }

  if (courtError) {
    return <div>Error: {courtError.message}</div>;
  }

  const pendingSubmittals = dashboardData?.statistics?.pending_submittals || [];
  const pendingRFIs = dashboardData?.statistics?.pending_rfis || [];
  const openSubmittals = openSubmittalsData?.submittals || [];
  const openRFIs = openRFIsData?.rfis || [];
  const hasCourtItems = pendingSubmittals.length > 0 || pendingRFIs.length > 0;
  const hasOpenItems = openSubmittals.length > 0 || openRFIs.length > 0;
  const courtTitleSuffix = `for ${dashboardData?.project_name || projectName}`;
  const isViewingAlternateProject =
    !!selectedHealthReportProjectId &&
    selectedHealthReportProjectId !== projectId;

  const healthReportColumns = [
    {
      title: "Project Name",
      dataIndex: "project_name",
      key: "project_name",
      width: 220,
    },
    {
      title: "PM",
      dataIndex: "pm_name",
      key: "pm_name",
      width: 180,
    },
    {
      title: "Total Submittals",
      dataIndex: "total_submittals",
      key: "total_submittals",
      width: 160,
    },
    {
      title: "Open / Pending",
      dataIndex: "open_pending_submittals",
      key: "open_pending_submittals",
      width: 180,
    },
    {
      title: "Open / R&R or Rejected",
      dataIndex: "open_revise_or_rejected_submittals",
      key: "open_revise_or_rejected_submittals",
      width: 280,
    },
    {
      title: "Closed",
      dataIndex: "closed_submittals",
      key: "closed_submittals",
      width: 120,
    },
  ];

  const handleRowClick = (type, item) => {
    if (!item) return;
    setSelectedItem(item);
    if (type === "submittal") {
      setShowSubmittalModal(true);
    } else if (type === "rfi") {
      setShowRfiModal(true);
    }
  };

  const formatSubmittalLabel = (submittal) => {
    const specSectionNumber =
      submittal?.specification_section_details?.section_number;
    const revision = submittal?.revision ?? 0;
    const numberWithRevision =
      submittal?.number !== undefined && submittal?.number !== null
        ? `${submittal.number}.${revision}`
        : null;
    const reference = [specSectionNumber, numberWithRevision]
      .filter(Boolean)
      .join("-");

    return [reference, submittal?.title].filter(Boolean).join(" - ");
  };

  const formatRFILabel = (rfi) => {
    const reference =
      rfi?.number !== undefined && rfi?.number !== null
        ? `RFI ${rfi.number}`
        : "RFI";
    return [reference, rfi?.title].filter(Boolean).join(" - ");
  };

  const renderListCard = (
    title,
    items,
    type,
    formatter,
    isClickable = true,
  ) => (
    <Card className="dashboard-table-card dashboard-court-card" title={title}>
      <div className="dashboard-court-list">
        {items.map((item) =>
          isClickable ? (
            <button
              key={item.id ?? item._id}
              type="button"
              className="dashboard-court-item"
              onClick={() => handleRowClick(type, item)}
            >
              <span className="dashboard-court-item-label">
                {formatter(item)}
              </span>
            </button>
          ) : (
            <div
              key={item.id ?? item._id}
              className="dashboard-court-item dashboard-court-item-static"
            >
              <span className="dashboard-court-item-label">
                {formatter(item)}
              </span>
            </div>
          ),
        )}
      </div>
    </Card>
  );

  const handleExportPDF = () => {
    try {
      const projects = healthReportData?.projects || [];

      if (projects.length === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Preparing project summary export...",
        duration: 2,
      });

      exportHealthReportToPDF(projects);
      notification.success({ message: "PDF exported successfully" });
    } catch (exportError) {
      notification.error({
        message: "Failed to export PDF",
        description: exportError.message,
      });
    }
  };

  return (
    <div className="dashboard dashboard-home">
      <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
      <ProjectSiteAddressBanner project={projectForAddress} />
      {canViewHealthReport && (
        <div className="dashboard-table-section">
          <div className="flex gap-4 mb-4">
            <Button icon={<DownloadOutlined />} onClick={handleExportPDF}>
              Export PDF
            </Button>
          </div>
          <Card className="dashboard-table-card">
            {isHealthReportError ? (
              <div>Error: {healthReportError.message}</div>
            ) : (
              <Table
                className="dashboard-compact-table"
                columns={healthReportColumns}
                dataSource={healthReportData?.projects || []}
                rowKey="project_id"
                rowSelection={{
                  type: "radio",
                  selectedRowKeys: effectiveProjectId
                    ? [effectiveProjectId]
                    : [],
                  onChange: (selectedRowKeys, selectedRows) => {
                    setSelectedHealthReportProjectId(
                      selectedRowKeys[0] || null,
                    );
                    setSelectedHealthReportProject(selectedRows[0] || null);
                    const project = currentUser?.projects?.find(
                      (p) => p.project_id === selectedRowKeys[0],
                    );
                    if (project) {
                      setSelectedProject(project);
                    }
                  },
                }}
                loading={isHealthReportLoading}
                pagination={false}
                scroll={{ x: 1140 }}
                size="small"
              />
            )}
          </Card>
        </div>
      )}

      {hasCourtItems ? (
        <div className="dashboard-court-grid">
          {pendingSubmittals.length > 0 &&
            renderListCard(
              `Submittals in Your Court ${courtTitleSuffix}`,
              pendingSubmittals,
              "submittal",
              formatSubmittalLabel,
              true,
            )}
          {pendingRFIs.length > 0 &&
            renderListCard(
              `RFIs in Your Court ${courtTitleSuffix}`,
              pendingRFIs,
              "rfi",
              formatRFILabel,
              true,
            )}
        </div>
      ) : (
        <Card className="dashboard-table-card dashboard-empty-card">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Nothing is in your court right now."
          />
        </Card>
      )}

      {hasOpenItems ? (
        <div className="dashboard-court-grid mt-4">
          {openSubmittals.length > 0 &&
            renderListCard(
              `All Open Submittals ${courtTitleSuffix}`,
              openSubmittals,
              "submittal",
              formatSubmittalLabel,
              true,
            )}
          {openRFIs.length > 0 &&
            renderListCard(
              `All Open RFIs ${courtTitleSuffix}`,
              openRFIs,
              "rfi",
              formatRFILabel,
              true,
            )}
        </div>
      ) : (
        <Card className="dashboard-table-card dashboard-empty-card mt-4">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No open submittals or RFIs for this project."
          />
        </Card>
      )}

      {showRfiModal && (
        <ViewRFIModal
          visible={showRfiModal}
          onClose={() => {
            setShowRfiModal(false);
            setSelectedItem(null);
          }}
          rfiId={selectedItem?.id ?? selectedItem?._id}
          projectInformation={selectedHealthReportProject}
        />
      )}
      {showSubmittalModal && (
        <ViewSubmittalModal
          visible={showSubmittalModal}
          onClose={() => {
            setShowSubmittalModal(false);
            setSelectedItem(null);
          }}
          submittalId={selectedItem?.id ?? selectedItem?._id}
          projectInformation={selectedHealthReportProject}
        />
      )}
    </div>
  );
};

export default Home;
